import { useContext, createContext, useRef, useMemo } from "react"
import { useDispatch } from "react-redux"
import { useLocation, useNavigate } from "react-router-dom"
import { useMutation } from "@tanstack/react-query"
import { Creators as ApiWrapperCreators } from "model/apiWrapper"
import { Creators as PreferencesCreators } from "model/preferences"
import { useAccountId } from "resources/account"
import { useLastPreferenceRedirect } from "resources/preferences"
import { v4 } from "uuid"
import { usePostQuery } from "./queries"
import { get } from "lodash"
import { combineTanStackStatuses } from "helpers/data-statuses"

const PreferenceContext = createContext(null)

export default PreferenceContext

export function useSaveContextPreferences(subType) {
  const context = useContext(PreferenceContext)
  const dispatch = useDispatch()
  const savePreferenceMutation = useMutation({
    mutationFn: (value) => {
      const payload = {
        items: [{
          accountId: context.accountId,
          key: context.key,
          type: `${context.type}.${subType}`,
          value,
        }],
      }
      return new Promise((resolve, reject) => {
        dispatch(ApiWrapperCreators.put("/api/users/me/preferences", payload, resolve, reject))
      })
    },
  })

  return savePreferenceMutation.mutate
}

export function useReadContextPreference(subType, defaultValue) {
  const accountId = useAccountId()
  const context = useContext(PreferenceContext)
  // We generate a unique ID, as we want to reload preferences everytime we load a screen.
  const ref = useRef(v4())

  const queryResults = usePostQuery({
    url: "/api/users/me/preferences",
    payload: {
      items: ([{
        accountId,
        key: context.key,
        type: `${context.type}.${subType}`,
      }]),
    },
    key: [ref.current],
    select: (queryResult) => get(queryResult, "0.value", defaultValue),
  })

  const queryStatus = combineTanStackStatuses([queryResults])

  return [queryResults.data, queryStatus]
}

/**
 * Saves a user preference scoped to the current account.
 */
export function useSavePreference(key, type) {
  const accountId = useAccountId()
  const dispatch = useDispatch()
  const savePreferenceMutation = useMutation({
    mutationFn: (value) => {
      const payload = {
        items: [{
          accountId,
          key,
          type,
          value,
        }],
      }
      return new Promise((resolve, reject) => {
        dispatch(ApiWrapperCreators.put("/api/users/me/preferences", payload, resolve, reject))
      })
    },
  })

  return savePreferenceMutation.mutate
}

/**
 * Retrieves a user preference scoped to the current account.
 */
export function useReadPreference(key, type, defaultValue) {
  const accountId = useAccountId()
  // We generate a unique ID, as we want to reload preferences everytime we load a screen.
  const ref = useRef(v4())

  const queryResults = usePostQuery({
    url: "/api/users/me/preferences",
    payload: {
      items: ([{
        accountId,
        key,
        type,
      }]),
    },
    key: [ref.current],
    select: (queryResult) => get(queryResult, "0.value", defaultValue),
  })

  const queryStatus = combineTanStackStatuses(queryResults)

  return [queryResults.data, queryStatus]
}

export function useCheckLastPreferenceRedirect() {
  const accountId = useAccountId()
  const { lastPreferenceRedirect } = useLastPreferenceRedirect()
  const location = useLocation()
  const navigate = useNavigate()
  const dispatch = useDispatch()

  const wasRedirectedFromPreferences = useMemo(() => lastPreferenceRedirect && location.pathname.startsWith(lastPreferenceRedirect.redirectedTo), [lastPreferenceRedirect, location])

  return {
    wasRedirectedFromPreferences,
    deletePreferenceAndRedirectToDefault: () => {
      if (lastPreferenceRedirect?.preferenceType) {
        dispatch(PreferencesCreators.deletePreferences([{ type: lastPreferenceRedirect.preferenceType, accountId }]))
        if (lastPreferenceRedirect?.defaultRedirectTo) {
          navigate(lastPreferenceRedirect.defaultRedirectTo, { replace: true })
        }
      }
    },
  }
}
